import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { URL } from '../../constants/url'
import axiosInstance from '../../utils/axiosInstance'
import { AxiosResponse } from 'axios'
import { RootState } from '../store'
import { toast } from 'react-toastify'
import { MyKnownError } from '../types/common'
import { Client } from './clients'
import { Cargo, fetchDeleteManyCargos, returnCargoToStore } from './cargos'

export interface Invoice {
  id: number
  client: Client
  client_id: number
  date: string
  paid: number
  cargos: Cargo[]
  sum: number
  pdf_url: string
  pdf_with_stamp_url: string
  signed: 1 | 0
  paid_at: string | null
  note: string | null
  third_id?: number
}

export interface InvoicesState {
  invoices: Invoice[]
  allInvoices: Invoice[] // Все счета без фильтров
  currentInvoice: Invoice | null
  isVisibleModalInvoice: boolean
  isPendingGetInvoices: boolean
  isPendingGetAllInvoices: boolean // Состояние загрузки всех счетов
  isPendingAddInvoice: boolean
  isPendingUpdateInvoice: boolean
  isPendingAttachCargo: boolean
  isPendingDetachCargo: boolean
  isVisibleDeleteModalInvoice: boolean
  isVisibleCreateModalInvoice: boolean
  showDeleteInvoiceChoice: boolean // Показывать ли модалку с выбором действия
  selectedCargosForInvoice: number[] // ID выбранных грузов для массовых операций
  isVisibleDetachCargosModal: boolean // Модалка подтверждения открепления грузов
  isVisibleDeleteCargosModal: boolean // Модалка подтверждения удаления грузов
}

export type InvoiceFilters = {
  date_from?: string
  date_to?: string
  country?: number
  paid?: number
  client?: string | number
}

const normalizeDate = (value?: string | null) => {
  if (!value) return ''
  return value.split('T')[0]
}

const doesInvoiceMatchFilters = (
  invoice: Invoice,
  filters?: InvoiceFilters
): boolean => {
  if (!filters) return true

  if (
    filters.client !== undefined &&
    filters.client !== null &&
    String(filters.client) !== '0' &&
    String(invoice.client_id) !== String(filters.client)
  ) {
    return false
  }

  if (
    filters.paid !== undefined &&
    Number(invoice.paid) !== Number(filters.paid)
  ) {
    return false
  }

  const invoiceDate = normalizeDate(invoice.date)
  if (filters.date_from && invoiceDate < normalizeDate(filters.date_from)) {
    return false
  }
  if (filters.date_to && invoiceDate > normalizeDate(filters.date_to)) {
    return false
  }

  // country в текущей модели счета невалидируем локально без полного запроса
  if (filters.country !== undefined && filters.country !== null) {
    return false
  }

  return true
}

const initialState: InvoicesState = {
  invoices: [],
  allInvoices: [],
  currentInvoice: null,
  isVisibleModalInvoice: false,
  isPendingGetInvoices: false,
  isPendingGetAllInvoices: false,
  isPendingAddInvoice: false,
  isPendingUpdateInvoice: false,
  isPendingAttachCargo: false,
  isPendingDetachCargo: false,
  isVisibleDeleteModalInvoice: false,
  isVisibleCreateModalInvoice: false,
  showDeleteInvoiceChoice: false,
  selectedCargosForInvoice: [],
  isVisibleDetachCargosModal: false,
  isVisibleDeleteCargosModal: false,
}

// Получение всех инвойсов
export const fetchInvoices = createAsyncThunk(
  'invoices/fetchInvoices',
  async (filters: InvoiceFilters | undefined, thunkAPI) => {
    try {
      const params = new URLSearchParams()

      if (filters) {
        if (filters.date_from) params.append('date_from', filters.date_from)
        if (filters.date_to) params.append('date_to', filters.date_to)
        if (filters.country)
          params.append('country', filters.country.toString())
        if (filters.paid !== undefined && filters.paid !== null) {
          params.append('paid', filters.paid.toString())
        }
        if (filters.client && filters?.client !== '0')
          params.append('client', filters.client.toString())
      }

      const url = filters
        ? `/${URL.invoices}?${params.toString()}`
        : `/${URL.invoices}`

      const response: AxiosResponse = await axiosInstance.get(url)
      if (response.statusText === 'OK') {
        return response
      }
      throw new Error()
    } catch (error) {
      const typedError = error as MyKnownError
      return thunkAPI.rejectWithValue(
        typedError?.response?.data?.message || 'Ошибка при загрузке счетов'
      )
    }
  }
)

// Получение всех инвойсов без фильтров (для выпадающих списков)
export const fetchAllInvoices = createAsyncThunk(
  'invoices/fetchAllInvoices',
  async (params: { client?: string } = {}, thunkAPI) => {
    try {
      let url = `/${URL.invoices}`

      // Если передан параметр клиента, добавляем его к URL
      if (params?.client) {
        url += `?client=${params.client}`
      }

      const response: AxiosResponse<Invoice[]> = await axiosInstance.get(url)
      if (response.statusText === 'OK') {
        return response.data
      }
      throw new Error()
    } catch (error) {
      const typedError = error as MyKnownError
      return thunkAPI.rejectWithValue(
        typedError?.response?.data?.message || 'Ошибка при загрузке всех счетов'
      )
    }
  }
)

export const fetchCurrentInvoices = createAsyncThunk(
  'invoices/fetchCurrentInvoices',
  async (id: number, thunkAPI) => {
    try {
      const response: AxiosResponse = await axiosInstance.get(
        `/${URL.invoices}/${id}`
      )
      if (response.statusText === 'OK') {
        return response.data as Invoice
      }
      throw new Error()
    } catch (error) {
      const typedError = error as MyKnownError
      return thunkAPI.rejectWithValue(
        typedError?.response?.data?.message || 'Ошибка при загрузке счетов'
      )
    }
  }
)

// Обновление инвойса
type UpdateInvoice = {
  date: string
  paid: number
  selectedCargos: number[]
  signed: boolean
  paidAt: string
  note: string | null
}
export const fetchUpdateInvoice = createAsyncThunk(
  'invoices/fetchUpdateInvoice',
  async (
    { date, paid, selectedCargos, signed, paidAt, note }: UpdateInvoice,
    thunkAPI
  ) => {
    const formData = new FormData()
    const {
      invoices: { currentInvoice },
    } = thunkAPI.getState() as RootState

    const factPaid = String(paid) === '1' ? paidAt : ''

    formData.append('date', date)
    formData.append('paid_at', factPaid)

    formData.append('signed', Boolean(signed) ? '1' : '0')
    formData.append('paid', String(paid))
    formData.append('note', String(note))

    selectedCargos.forEach((id, index) => {
      formData.append(`cargos[${index}]`, id.toString())
    })

    if (currentInvoice?.id) {
      formData.set('_method', 'put')
    }

    try {
      const response: AxiosResponse = await axiosInstance.post(
        `/${URL.invoices}${currentInvoice?.id ? `/${currentInvoice.id}` : ''}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      if (response.statusText === 'OK') {
        return response.data
      }
      throw new Error()
    } catch (error) {
      const typedError = error as MyKnownError
      return thunkAPI.rejectWithValue(
        typedError?.response?.data?.message || 'Ошибка при обновлении счета'
      )
    }
  }
)

// Прикрепление груза к инвойсу
export const fetchAttachCargo = createAsyncThunk(
  'invoices/fetchAttachCargo',
  async (
    {
      invoiceId,
      selectedCargos,
    }: { invoiceId: string; selectedCargos: number[] },
    thunkAPI
  ) => {
    try {
      const formData = new FormData()
      selectedCargos.forEach((id, index) => {
        formData.append(`cargos[${index}]`, id.toString())
      })

      formData.set('_method', 'put')

      const response: AxiosResponse = await axiosInstance.post(
        `${URL.invoices}/${invoiceId}/attach`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      if (response.status === 200 || response.status === 201) {
        return { invoiceId, invoice: response.data }
      }
      throw new Error(`Unexpected status code: ${response.status}`)
    } catch (error) {
      console.error('Error in fetchAttachCargo:', error)
      const typedError = error as MyKnownError
      return thunkAPI.rejectWithValue(
        typedError?.response?.data?.message || 'Ошибка при прикреплении груза'
      )
    }
  }
)

// Создание нового инвойса
export const fetchAddInvoice = createAsyncThunk(
  'invoices/fetchAddInvoice',
  async (
    { selectedCargos, date }: { selectedCargos: number[]; date?: string },
    thunkAPI
  ) => {
    const formData = new FormData()

    selectedCargos.forEach((id, index) => {
      formData.append(`cargos[${index}]`, id.toString())
    })
    if (date) formData.append('date', date)

    try {
      const response: AxiosResponse = await axiosInstance.post(
        `/${URL.invoices}`,
        formData
      )
      if (response.statusText === 'OK' || response.statusText === 'Created') {
        return response.data
      }
      throw new Error()
    } catch (error) {
      const typedError = error as MyKnownError
      return thunkAPI.rejectWithValue(
        typedError?.response?.data?.message || 'Ошибка при создании счета'
      )
    }
  }
)

// Открепление груза от инвойса
export const fetchDetachCargo = createAsyncThunk(
  'invoices/fetchDetachCargo',
  async (
    {
      invoiceId,
      selectedCargos,
    }: { invoiceId: string; selectedCargos: number[] },
    thunkAPI
  ) => {
    try {
      const formData = new FormData()
      selectedCargos.forEach((id, index) => {
        formData.append(`cargos[${index}]`, id.toString())
      })

      formData.set('_method', 'put')

      const response: AxiosResponse = await axiosInstance.post(
        `${URL.invoices}/${invoiceId}/detach`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      if (response.status === 200 || response.status === 201) {
        return { invoiceId, invoice: response.data }
      }
      throw new Error(`Unexpected status code: ${response.status}`)
    } catch (error) {
      console.error('Error in fetchAttachCargo:', error)
      const typedError = error as MyKnownError
      return thunkAPI.rejectWithValue(
        typedError?.response?.data?.message || 'Ошибка при прикреплении груза'
      )
    }
  }
)

export const fetchDeleteInvoice = createAsyncThunk(
  'invoices/fetchDeleteInvoice',
  async (action: 'return' | 'delete', thunkAPI) => {
    const {
      invoices: { currentInvoice },
    } = thunkAPI.getState() as RootState

    // Получаем все id грузов из currentInvoice
    const cargoIds = currentInvoice?.cargos?.map((cargo) => cargo.id) || []
    const cargosToReturn =
      action === 'return'
        ? currentInvoice?.cargos?.filter((cargo) =>
            cargoIds.includes(cargo.id)
          ) || []
        : []
    const hasCargos = cargoIds.length > 0

    try {
      let response: AxiosResponse

      if (action === 'return') {
        if (hasCargos) {
          try {
            await thunkAPI
              .dispatch(
                fetchDetachCargo({
                  invoiceId: currentInvoice?.id?.toString() || '',
                  selectedCargos: cargoIds,
                })
              )
              .unwrap()
          } catch (error) {
            console.error('Ошибка при откреплении грузов:', error)
            return thunkAPI.rejectWithValue(
              'Ошибка при откреплении грузов от счёта'
            )
          }
        }

        response = await axiosInstance.delete(
          `${URL.invoices}/${currentInvoice?.id}`
        )
      } else {
        if (hasCargos) {
          try {
            await thunkAPI.dispatch(fetchDeleteManyCargos(cargoIds)).unwrap()
          } catch (error) {
            console.error('Ошибка при удалении грузов:', error)
            return thunkAPI.rejectWithValue('Ошибка при удалении грузов')
          }
        }

        response = await axiosInstance.delete(
          `${URL.invoices}/${currentInvoice?.id}`
        )
      }

      if (
        response.statusText === 'OK' ||
        response.status === 200 ||
        response.status === 201
      ) {
        if (action === 'return' && cargosToReturn.length > 0) {
          cargosToReturn.forEach((cargo) => {
            thunkAPI.dispatch(returnCargoToStore(cargo))
          })
        }
        return { ...response.data, id: currentInvoice?.id, action }
      }
      throw new Error()
    } catch (error) {
      console.error('Ошибка при отправке данных:', error)
      return thunkAPI.rejectWithValue('Ошибка при отправке данных')
    }
  }
)

export const invoicesSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    setCurrentInvoice: (state, action) => {
      return { ...state, currentInvoice: action.payload }
    },

    setIsVisibleModalInvoice: (state, action) => {
      return {
        ...state,
        isVisibleModalInvoice: action.payload,
      }
    },

    setIsVisibleCreateModalInvoice: (state, action) => {
      return {
        ...state,
        isVisibleCreateModalInvoice: action.payload,
      }
    },

    setVisibleDeleteModalInvoice: (
      state,
      action: PayloadAction<Invoice | boolean>
    ) => {
      // Если передали счёт - показываем модалку с выбором действия
      // Если передали boolean - просто показываем/скрываем модалку
      if (typeof action.payload === 'boolean') {
        return {
          ...state,
          isVisibleDeleteModalInvoice: action.payload,
          showDeleteInvoiceChoice: false,
        }
      } else {
        // Передался счёт - определяем, нужно ли показывать выбор действия
        const hasCargos =
          action.payload.cargos && action.payload.cargos.length > 0
        return {
          ...state,
          isVisibleDeleteModalInvoice: true,
          currentInvoice: action.payload,
          showDeleteInvoiceChoice: hasCargos,
        }
      }
    },

    setSelectedCargosForInvoice: (state, action: PayloadAction<number[]>) => {
      return { ...state, selectedCargosForInvoice: action.payload }
    },

    clearSelectedCargosForInvoice: (state) => {
      return { ...state, selectedCargosForInvoice: [] }
    },

    setVisibleDetachCargosModal: (state, action: PayloadAction<boolean>) => {
      return { ...state, isVisibleDetachCargosModal: action.payload }
    },

    setVisibleDeleteCargosModal: (state, action: PayloadAction<boolean>) => {
      return { ...state, isVisibleDeleteCargosModal: action.payload }
    },
    upsertCreatedInvoiceInStore: (
      state,
      action: PayloadAction<{ invoice: Invoice; filters?: InvoiceFilters }>
    ) => {
      const { invoice, filters } = action.payload
      const allInvoicesWithoutCurrent = state.allInvoices.filter(
        (item) => item.id !== invoice.id
      )
      const invoicesWithoutCurrent = state.invoices.filter(
        (item) => item.id !== invoice.id
      )

      state.allInvoices = [invoice, ...allInvoicesWithoutCurrent]

      if (doesInvoiceMatchFilters(invoice, filters)) {
        state.invoices = [invoice, ...invoicesWithoutCurrent]
      }
    },
  },
  extraReducers: (builder) => {
    // Получение инвойсов
    builder.addCase(fetchInvoices.pending, (state) => {
      return { ...state, isPendingGetInvoices: true }
    })
    builder.addCase(fetchInvoices.fulfilled, (state, action) => {
      const sortedInvoices = [...action.payload.data].sort(
        (a, b) => b.id - a.id
      )
      return {
        ...state,
        invoices: sortedInvoices,
        isPendingGetInvoices: false,
      }
    })
    builder.addCase(fetchInvoices.rejected, (state, action) => {
      toast.error(action.payload as string)
      return { ...state, isPendingGetInvoices: false }
    })

    // Получение всех инвойсов
    builder.addCase(fetchAllInvoices.pending, (state) => {
      return { ...state, isPendingGetAllInvoices: true }
    })
    builder.addCase(fetchAllInvoices.fulfilled, (state, action) => {
      const sortedInvoices = [...action.payload].sort(
        (a, b) => b.id - a.id
      )
      return {
        ...state,
        allInvoices: sortedInvoices,
        isPendingGetAllInvoices: false,
      }
    })
    builder.addCase(fetchAllInvoices.rejected, (state, action) => {
      toast.error(action.payload as string)
      return { ...state, isPendingGetAllInvoices: false }
    })

    builder.addCase(fetchCurrentInvoices.pending, (state) => {
      return { ...state, isPendingGetInvoices: true }
    })
    builder.addCase(fetchCurrentInvoices.fulfilled, (state, action) => {
      return {
        ...state,
        currentInvoice: action.payload,
        isPendingGetInvoices: false,
      }
    })
    builder.addCase(fetchCurrentInvoices.rejected, (state, action) => {
      toast.error(action.payload as string)
      return { ...state, isPendingGetInvoices: false }
    })

    // Добавление инвойса
    builder.addCase(fetchAddInvoice.pending, (state) => {
      return { ...state, isPendingAddInvoice: true }
    })
    builder.addCase(fetchAddInvoice.fulfilled, (state, action) => {
      toast.success('Счет успешно создан!')
      const createdInvoice = action.payload as Invoice
      const allInvoicesWithoutCurrent = state.allInvoices.filter(
        (item) => item.id !== createdInvoice.id
      )
      return {
        ...state,
        // invoices обновляется отдельным синхронным action с учетом фильтров на странице
        allInvoices: [createdInvoice, ...allInvoicesWithoutCurrent],
        isPendingAddInvoice: false,
        isVisibleModalInvoice: false,
      }
    })
    builder.addCase(fetchAddInvoice.rejected, (state, action) => {
      toast.error(action.payload as string)
      return { ...state, isPendingAddInvoice: false }
    })

    // Обновление инвойса
    builder.addCase(fetchUpdateInvoice.pending, (state) => {
      return { ...state, isPendingUpdateInvoice: true }
    })
    builder.addCase(fetchUpdateInvoice.fulfilled, (state, action) => {
      // Защита от потери грузов - если сервер вернул пустой массив грузов,
      // но у нас были грузы в currentInvoice, сохраняем их
      let updatedInvoice = { ...action.payload }
      if (
        action.payload.cargos.length === 0 &&
        state.currentInvoice?.cargos &&
        state.currentInvoice.cargos.length > 0
      ) {
        console.warn('Server returned empty cargos, preserving existing cargos')
        updatedInvoice.cargos = state.currentInvoice.cargos
      }

      toast.success('Счет успешно обновлен!')
      return {
        ...state,
        invoices: state.invoices.map((invoice) =>
          invoice.id === action.payload.id ? updatedInvoice : invoice
        ),
        allInvoices: state.allInvoices.map((invoice) =>
          invoice.id === action.payload.id ? updatedInvoice : invoice
        ),
        currentInvoice: updatedInvoice,
        isPendingUpdateInvoice: false,
        isVisibleModalInvoice: false,
      }
    })
    builder.addCase(fetchUpdateInvoice.rejected, (state, action) => {
      toast.error(action.payload as string)
      return { ...state, isPendingUpdateInvoice: false }
    })

    // Прикрепление груза
    builder.addCase(fetchAttachCargo.pending, (state) => {
      return { ...state, isPendingAttachCargo: true }
    })
    builder.addCase(fetchAttachCargo.fulfilled, (state, action) => {
      toast.success('Груз успешно прикреплен!')
      return {
        ...state,
        invoices: state.invoices.map((invoice) =>
          String(invoice.id) === action.payload.invoiceId
            ? action.payload.invoice
            : invoice
        ),

        isPendingAttachCargo: false,
      }
    })
    builder.addCase(fetchAttachCargo.rejected, (state, action) => {
      toast.error(action.payload as string)
      return { ...state, isPendingAttachCargo: false }
    })

    // Открепление груза
    builder.addCase(fetchDetachCargo.pending, (state) => {
      return { ...state, isPendingDetachCargo: true }
    })
    builder.addCase(fetchDetachCargo.fulfilled, (state, action) => {
      toast.success('Груз успешно откреплен!')
      const updatedInvoice = action.payload.invoice
      return {
        ...state,
        invoices: state.invoices.map((invoice) =>
          String(invoice.id) === action.payload.invoiceId
            ? updatedInvoice
            : invoice
        ),
        allInvoices: state.allInvoices.map((invoice) =>
          String(invoice.id) === action.payload.invoiceId
            ? updatedInvoice
            : invoice
        ),
        currentInvoice:
          state.currentInvoice &&
          String(state.currentInvoice.id) === action.payload.invoiceId
            ? updatedInvoice
            : state.currentInvoice,
        isPendingDetachCargo: false,
      }
    })
    builder
      .addCase(fetchDetachCargo.rejected, (state, action) => {
        toast.error(action.payload as string)
        return { ...state, isPendingDetachCargo: false }
      })

      .addCase(fetchDeleteInvoice.pending, (state) => {
        //   state.isPendingDeleteIncoming = true
        return { ...state, isPendingDeleteInvoices: true }
      })

      .addCase(
        fetchDeleteInvoice.fulfilled,
        (
          state,
          action: PayloadAction<{ id: number; action: 'return' | 'delete' }>
        ) => {
          if (action.payload.action === 'return') {
            toast.success('Счёт успешно удалён')
            // Удаляем счёт из списка, так как он был удалён
            return {
              ...state,
              isPendingDeleteInvoices: false,
              isVisibleDeleteModalInvoice: false,
              showDeleteInvoiceChoice: false,
              invoices:
                state?.invoices &&
                state?.invoices.filter((item) => item.id !== action.payload.id),
              allInvoices:
                state?.allInvoices &&
                state?.allInvoices.filter(
                  (item) => item.id !== action.payload.id
                ),
              currentInvoice: null,
            }
          } else {
            toast.success('Удалено!')
            return {
              ...state,
              isPendingDeleteInvoices: false,
              isVisibleDeleteModalInvoice: false,
              showDeleteInvoiceChoice: false,
              invoices:
                state?.invoices &&
                state?.invoices.filter((item) => item.id !== action.payload.id),
              allInvoices:
                state?.allInvoices &&
                state?.allInvoices.filter(
                  (item) => item.id !== action.payload.id
                ),
            }
          }
        }
      )

      .addCase(fetchDeleteInvoice.rejected, (state, action) => {
        toast.error(action.payload as string)
        return {
          ...state,
          isPendingDeleteInvoices: false,
          isVisibleDeleteModalInvoice: false,
          showDeleteInvoiceChoice: false,
        }
      })
  },
})

export const {
  setCurrentInvoice,
  setIsVisibleModalInvoice,
  setVisibleDeleteModalInvoice,
  setIsVisibleCreateModalInvoice,
  setSelectedCargosForInvoice,
  clearSelectedCargosForInvoice,
  setVisibleDetachCargosModal,
  setVisibleDeleteCargosModal,
  upsertCreatedInvoiceInStore,
} = invoicesSlice.actions

export default invoicesSlice.reducer
